// src/components/HighlightedTTS.jsx

import React, { useEffect, useId, useMemo, useState, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import MathContent from './MathContent';
import { containsMathContent, mathContentToSpeech } from '../utils/mathContent';
import {
  createSpeechDisplayMap,
  getTtsCapability,
  isAssessNowTabletApp,
  mapDisplayTokensToSpeech,
  normalizeSpeechText,
  observeTtsCapability,
  resolveDisplaySpeechBoundaryRange,
  startSpeech
} from '../utils/ttsRuntime';

export default function HighlightedTTS({
  text = "",
  enabled = true,
  size = 'medium',
  containerStyle = {},
  textStyle = {},
  highlightedStyle = {},
  buttonClassName = "",
  speechText = '',
  hideText = false,
  ariaLabel = '',
  renderTokens,
  renderContent
}) {
  const [speaking, setSpeaking] = useState(false);
  const [activeSpeechRange, setActiveSpeechRange] = useState(null);
  const [highlightMode, setHighlightMode] = useState('idle');
  const [capability, setCapability] = useState(() => getTtsCapability());
  const [statusMessage, setStatusMessage] = useState('');
  const sessionRef = useRef(null);
  const speechRunRef = useRef(0);
  const statusId = useId();

  const normalizedText = String(text ?? '');
  const hasMath = containsMathContent(normalizedText);
  const normalizedSpeechText = mathContentToSpeech(normalizedText, speechText);
  const speechDisplayMap = useMemo(
    () => createSpeechDisplayMap(normalizedText, normalizedSpeechText),
    [normalizedText, normalizedSpeechText]
  );
  const canHighlightSpeech = !hasMath
    && speechDisplayMap.displayTokens.length > 0
    && (speechDisplayMap.direct || speechDisplayMap.coverage >= 0.55);
  const displayTokens = useMemo(() => mapDisplayTokensToSpeech(normalizedText), [normalizedText]);
  const speechSupported = capability.available;

  useEffect(() => observeTtsCapability(nextCapability => {
    setCapability(nextCapability);
    if (nextCapability.available) setStatusMessage('');
  }), []);

  useEffect(() => {
    // A visibility/TTS change or a new question invalidates the current
    // utterance immediately. Reset UI state during the new effect; cleanup is
    // kept state-free so unmounting cannot schedule a React update.
    speechRunRef.current += 1;
    sessionRef.current?.stop('content-changed');
    sessionRef.current = null;
    setSpeaking(false);
    setActiveSpeechRange(null);
    setHighlightMode('idle');
    setStatusMessage('');

    return () => {
      speechRunRef.current += 1;
      sessionRef.current?.stop('unmounted');
      sessionRef.current = null;
    };
  }, [enabled, normalizedText, normalizedSpeechText]);

  if (!enabled) {
    if (hideText) return null;
    return (
      <div
        style={{
          ...containerStyle,
          whiteSpace: 'pre-wrap',
        }}
      >
        <MathContent text={normalizedText} as="div" style={textStyle} />
      </div>
    );
  }

  const buttonSizes = {
    small: 44,
    medium: 48,
    large: 52,
  };

  const handleSpeak = () => {
    if (!enabled || !normalizedSpeechText) return;

    if (speaking && sessionRef.current) {
      speechRunRef.current += 1;
      sessionRef.current.stop('stopped');
      sessionRef.current = null;
      setSpeaking(false);
      setActiveSpeechRange(null);
      setHighlightMode('idle');
      setStatusMessage('');
      return;
    }

    const speechRun = speechRunRef.current + 1;
    speechRunRef.current = speechRun;
    setStatusMessage('');
    try {
      const session = startSpeech({
        text: normalizedSpeechText,
        onStart: () => {
          if (speechRunRef.current !== speechRun) return;
          setSpeaking(true);
          setHighlightMode('waiting');
          if (canHighlightSpeech) {
            setActiveSpeechRange(resolveDisplaySpeechBoundaryRange(
              { name: 'word', charIndex: 0 },
              normalizedText,
              normalizedSpeechText,
              speechDisplayMap
            ));
          }
        },
        onEnd: () => {
          if (speechRunRef.current !== speechRun) return;
          sessionRef.current = null;
          setSpeaking(false);
          setActiveSpeechRange(null);
          setHighlightMode('idle');
        },
        onError: error => {
          if (speechRunRef.current !== speechRun) return;
          sessionRef.current = null;
          setSpeaking(false);
          setActiveSpeechRange(null);
          setHighlightMode('idle');
          setCapability(getTtsCapability());
          setStatusMessage(isAssessNowTabletApp()
            ? ''
            : error?.message || 'Read aloud could not start. Check the tablet speech settings and try again.');
        },
        onBoundary: event => {
          if (!canHighlightSpeech || speechRunRef.current !== speechRun) return;
          setActiveSpeechRange(resolveDisplaySpeechBoundaryRange(
            event,
            normalizedText,
            normalizedSpeechText,
            speechDisplayMap
          ));
          setHighlightMode(event?.estimated ? 'estimated' : 'engine');
        }
      });
      sessionRef.current = session;
      setCapability(getTtsCapability());
      setSpeaking(true);
    } catch (error) {
      sessionRef.current = null;
      setSpeaking(false);
      setActiveSpeechRange(null);
      setHighlightMode('idle');
      setCapability(getTtsCapability());
      setStatusMessage(isAssessNowTabletApp()
        ? ''
        : error?.message || 'Read aloud is unavailable on this tablet.');
    }
  };

  const buttonSize = buttonSizes[size] || buttonSizes.medium;
  const title = !normalizedSpeechText
    ? 'No readable text is available'
    : !speechSupported
      ? 'Check tablet read-aloud service'
      : speaking
        ? 'Stop speaking'
        : `Read aloud using the ${capability.label}`;

  return (
    <div>
      {/* TTS Play/Stop Button */}
      <button
        type="button"
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          handleSpeak();
        }}
        disabled={!normalizedSpeechText}
        className={`
          flex items-center justify-center rounded-full 
          hover:bg-gray-100 transition-colors duration-200
          ${speaking ? 'bg-blue-100 text-blue-600' : speechSupported ? 'text-gray-600' : 'bg-amber-50 text-amber-700'}
          ${buttonClassName}
        `}
        style={{
          width: buttonSize,
          height: buttonSize,
          minWidth: 44,
          minHeight: 44,
          touchAction: 'manipulation',
          border: speechSupported ? '1px solid transparent' : '1px solid #f59e0b',
          cursor: normalizedSpeechText ? 'pointer' : 'not-allowed'
        }}
        title={title}
        aria-label={speaking
          ? ariaLabel ? `Stop speaking: ${ariaLabel}` : 'Stop speaking'
          : speechSupported
            ? ariaLabel || 'Read aloud'
            : ariaLabel ? `Check read aloud availability: ${ariaLabel}` : 'Check read aloud availability'}
        aria-describedby={statusMessage ? statusId : undefined}
        aria-pressed={speaking}
        data-tts-engine={capability.engine}
        data-tts-available={speechSupported ? 'true' : 'false'}
        data-tts-highlight-mode={highlightMode}
      >
        {speaking ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
      </button>

      {statusMessage && (
        <div
          id={statusId}
          role="alert"
          style={{
            marginTop: 6,
            maxWidth: 360,
            padding: '8px 10px',
            borderRadius: 8,
            border: '1px solid #f59e0b',
            background: '#fffbeb',
            color: '#78350f',
            fontSize: '.82rem',
            fontWeight: 650,
            lineHeight: 1.35
          }}
        >
          {statusMessage}
        </div>
      )}

      {/* Render tokens with highlight for word tokens, plus 
          preserve spacing via white-space: pre-wrap. */}
      {!hideText && (
        <div style={{
          ...containerStyle,
          whiteSpace: 'pre-wrap',
          ...(!canHighlightSpeech && speaking ? highlightedStyle : {})
        }}>
          {typeof renderContent === 'function' ? renderContent({
            activeSpeechRange,
            canHighlightSpeech,
            highlightMode,
            speaking
          }) : hasMath ? (
            <MathContent text={normalizedText} as="div" style={textStyle} />
          ) : typeof renderTokens === 'function' ? renderTokens({
            tokens: displayTokens.map(token => ({
              ...token,
              isActive: !token.whitespace
                && Boolean(activeSpeechRange)
                && token.start < activeSpeechRange.end
                && token.end > activeSpeechRange.start
            })),
            activeSpeechRange,
            highlightedStyle,
            highlightMode,
            speaking,
            textStyle
          }) : displayTokens.map((token, i) => {
            const isActive = !token.whitespace
              && activeSpeechRange
              && token.start < activeSpeechRange.end
              && token.end > activeSpeechRange.start;
            return (
              <span
                key={i}
                style={
                  isActive
                    ? { ...textStyle, ...highlightedStyle }
                    : { ...textStyle }
                }
              >
                {token.text}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
